import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { analyticsApi } from '../../services/api'
import { formatRegulationName } from '../../utils/regulationLabels'

const NON_ALPHANUMERIC_RUN_PATTERN = /[^a-z0-9]+/g
const EDGE_HYPHEN_PATTERN = /^-|-$/g

// The State filter's vocabulary. A state is one of the brand's places (a
// Kambi regulation, "Pennsylvania"), listed by the API with its key, name and
// postal code. The page reads every state until some are picked; several
// picked are added together.

// A brand with one state has nothing to pick.
export const STATE_PICKER_MIN = 2

const NO_STATES = Object.freeze([])

// The brand's states, by name. They change with the brand's offering
// mappings, not with the range. The same empty list until they load (or
// when they can't), so memos built on it don't recompute every render.
export function useOrgStates(slug, { enabled = true } = {}) {
  const query = useQuery({
    queryKey: ['growth', 'states', slug],
    queryFn: () => analyticsApi.getStates(slug),
    enabled: Boolean(slug) && enabled,
    staleTime: 10 * 60_000,
  })
  return query.data?.states || NO_STATES
}

// The picker shows when there is a choice, or when the URL picked states the
// brand doesn't list (a link from another brand), so they can be cleared.
export function showsStatePicker(states, picked) {
  return states.length >= STATE_PICKER_MIN || picked.length > 0
}

// One state as the brand lists it, or a stand-in named after its key.
// A state the brand doesn't list (a link from another brand) is named the
// way the API names states: a regulation humanized (US_TEXAS: Texas), any
// other code as it is.
export function stateOf(key, states) {
  return states.find((state) => state.key === key) || { key, label: formatRegulationName(key) || key, code: '' }
}

// The states the picker lists: the brand's, then any picked state the brand
// doesn't list (a link from another brand), ticked, so it can be cleared.
export function listedStates(states, picked) {
  const unlisted = picked.filter((key) => !states.some((state) => state.key === key))
  return [...states, ...unlisted.map((key) => stateOf(key, states))]
}

// The picker's label: "All states", the one state's name, or the picked
// states' codes ("PA, NJ, NY +2"); "2 states" while the names load.
export function statesLabel(picked, states, { shown = 3 } = {}) {
  if (!picked.length) return 'All states'
  // The names arrive with the brand's list; until then (a filtered page
  // reloading) say how many, never the internal keys.
  if (!states.length) return picked.length === 1 ? '1 state' : `${picked.length} states`
  if (picked.length === 1) return stateOf(picked[0], states).label
  const names = picked.map((key) => {
    const state = stateOf(key, states)
    return state.code || state.label
  })
  const rest = names.length - shown
  return rest > 0 ? `${names.slice(0, shown).join(', ')} +${rest}` : names.join(', ')
}

// An export's file name with the states it was narrowed to:
// "feed-impressions-2026-09-01.csv" read for PA and NJ is
// "feed-impressions-2026-09-01-pa-nj.csv".
export function exportFilename(filename, picked, states) {
  if (!picked?.length) return filename
  const slug = picked
    .map((key) => {
      const state = stateOf(key, states)
      return (state.code || state.label).toLowerCase().replace(NON_ALPHANUMERIC_RUN_PATTERN, '-').replace(EDGE_HYPHEN_PATTERN, '')
    })
    .filter(Boolean)
    .join('-')
  if (!slug) return filename
  const dot = filename.lastIndexOf('.')
  return dot > 0 ? `${filename.slice(0, dot)}-${slug}${filename.slice(dot)}` : `${filename}-${slug}`
}

// The states each dashboard was last read with, kept per viewer in this
// browser: they are view state, like a card's lines, not part of the
// layout everyone shares. Storage can be missing or refuse a write; then a
// dashboard simply opens on every state.
const SAVED_PREFIX = 'growth:states:'

export function readSavedStates(scope) {
  try {
    const saved = JSON.parse(window.localStorage.getItem(SAVED_PREFIX + scope))
    return Array.isArray(saved) ? saved.filter((key) => typeof key === 'string' && key) : null
  } catch {
    return null
  }
}

export function saveStates(scope, states) {
  try {
    window.localStorage.setItem(SAVED_PREFIX + scope, JSON.stringify(states))
  } catch {
    // Nothing to keep it in: the selection lasts as long as the URL.
  }
}

// Each dashboard keeps the states it was last read with. Switching to a
// dashboard brings back its states (or every state); arriving through a link
// that names states keeps the link's. `scope` is the operator, brand and
// dashboard, null while they load; `fromLink` says the URL named states.
export function useDashboardStates({ scope, picked, fromLink, onChange }) {
  const shown = useRef(null)
  const key = picked.join(',')
  useEffect(() => {
    if (!scope) return
    const switched = shown.current !== scope
    // The first dashboard shown, reached through a link that names states.
    const keepsLink = shown.current === null && fromLink
    shown.current = scope
    if (switched && !keepsLink) {
      const saved = readSavedStates(scope) || []
      // Restore first; the next run, with the URL updated, saves it.
      if (saved.join(',') !== key) {
        onChange(saved)
        return
      }
    }
    saveStates(scope, picked)
  }, [scope, key]) // eslint-disable-line react-hooks/exhaustive-deps
}
